import type { DisplayDoc, DocNode, PublicDocId } from './api';

/** Bundles the rendered thread tree with derived thread display metadata. */
export interface DocThreadView {
  /** Contains the rendered thread tree in the same order used by the doc page. */
  docTree: DocNode[];
  /** Identifies the visible bottom non-feedback doc used for omnibox replies. */
  visibleBottomDocId: PublicDocId | null;
}

/**
 * Selects the citation that defines thread parentage.
 *
 * This prefers explicit `reply_to` relations, while falling back to the legacy
 * unspecified relation emitted by the backend today for normal replies during
 * ENG-986 migration.
 */
function getThreadParentCitation(doc: DisplayDoc): DisplayDoc['citations'][number] | undefined {
  return (
    doc.citations.find((citation) => citation.rel === 'reply_to' || citation.rel === 'ReplyTo') ??
    doc.citations.find(
      (citation) => citation.rel === 'Unspecified' || citation.rel === 'unspecified'
    )
  );
}

/** Links a flat thread response into an unsorted tree structure. */
function linkDocTree(docs: DisplayDoc[]): DocNode[] {
  const docMap = new Map<string, DocNode>();
  const rootNodes: DocNode[] = [];

  docs.forEach((doc) => {
    docMap.set(doc.id, { ...doc, children: [] });
  });

  docs.forEach((doc) => {
    const node = docMap.get(doc.id);
    const threadParentCitation = getThreadParentCitation(doc);

    if (node && threadParentCitation) {
      const parentDocId = threadParentCitation.doc;
      const parent = docMap.get(parentDocId);

      if (parent) {
        parent.children.push(node);
      } else {
        rootNodes.push(node);
      }
    } else if (node) {
      rootNodes.push(node);
    }
  });

  return rootNodes;
}

/** Sorts a subtree into render order and returns its visible bottom doc id. */
function finalizeNode(node: DocNode): PublicDocId | null {
  node.children.sort(sortByDate);

  let visibleBottomDocId: PublicDocId | null = node.is_feedback_post ? null : node.id;
  for (const child of node.children) {
    const childVisibleBottomDocId = finalizeNode(child);
    if (!node.is_feedback_post && childVisibleBottomDocId) {
      visibleBottomDocId = childVisibleBottomDocId;
    }
  }

  return visibleBottomDocId;
}

/** Sorts roots into render order and returns the visible bottom doc id. */
function finalizeDocTree(rootNodes: DocNode[]): PublicDocId | null {
  rootNodes.sort(sortByDate);

  let visibleBottomDocId: PublicDocId | null = null;
  for (const rootNode of rootNodes) {
    const rootVisibleBottomDocId = finalizeNode(rootNode);
    if (rootVisibleBottomDocId) {
      visibleBottomDocId = rootVisibleBottomDocId;
    }
  }

  return visibleBottomDocId;
}

/** Sorts docs into the same render order used by the thread page. */
export function buildDocTree(docs: DisplayDoc[]): DocNode[] {
  const docTree = linkDocTree(docs);
  finalizeDocTree(docTree);
  return docTree;
}

/** Compares nodes by creation time so thread rendering stays deterministic. */
const sortByDate = (a: DocNode, b: DocNode) =>
  new Date(a.provenance.createdon).getTime() - new Date(b.provenance.createdon).getTime();

/** Builds the rendered thread tree once and derives reply-target metadata from it. */
export function buildDocThreadView(docs: DisplayDoc[]): DocThreadView {
  const docTree = linkDocTree(docs);
  const visibleBottomDocId = finalizeDocTree(docTree);

  return {
    docTree,
    visibleBottomDocId,
  };
}
